import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Avatar, Badge } from '../components/ui.js';
import { Icon } from '../components/Icon.js';

interface CustomerRow {
  customer_id: string;
  phone: string | null;
  name: string | null;
  email: string | null;
  tags: string[];
  total_orders: number;
  total_appointments: number;
  last_contact_at: string;
}

export function Customers() {
  const api = useApi();
  const { t, lang } = useLang();
  const [q, setQ] = useState('');
  const { data } = useQuery({
    queryKey: ['customers', q],
    queryFn: () => api.get<{ items: CustomerRow[] }>(`/customers?${new URLSearchParams(q ? { q } : {})}`),
  });
  const rows = data?.items ?? [];
  const dateLocale = lang === 'ar' ? 'ar-EG' : 'en-US';

  return (
    <div className="wrap section-gap">
      <PageHead
        title={t('page.customers.title')}
        sub={t('page.customers.sub')}
        actions={
          <div className="inbox__search" style={{ margin: 0, minWidth: 240 }}>
            <Icon name="search" />
            <input placeholder={t('page.customers.search.ph')} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        }
      />
      <div className="card tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('page.customers.col.customer')}</th>
              <th>{t('page.customers.col.phone')}</th>
              <th>{t('page.customers.col.orders')}</th>
              <th>{t('page.customers.col.tags')}</th>
              <th>{t('page.customers.col.lastContact')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.customer_id}>
                <td>
                  <div className="usercell">
                    <Avatar initial={(c.name?.[0] ?? '?').toUpperCase()} size={36} grad={false} />
                    <div><div className="usercell__name">{c.name ?? '—'}</div>{c.email && <div className="usercell__meta">{c.email}</div>}</div>
                  </div>
                </td>
                <td className="mono" dir="ltr" style={{ textAlign: 'start' }}>{c.phone ?? '—'}</td>
                <td className="tbl__cell-strong">{c.total_orders}</td>
                <td>
                  <div className="flex gap8 wrapf">
                    {c.tags.length === 0 ? <span className="muted">—</span> :
                      c.tags.map((tag, j) => <Badge key={j} kind={tag === 'VIP' ? 'accent' : 'muted'} plain>{tag}</Badge>)}
                  </div>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>{new Date(c.last_contact_at).toLocaleDateString(dateLocale)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 28, color: 'var(--ink-3)' }}>{t('page.customers.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
